<template>
  <div class="selection:bg-brand-500 selection:text-white pb-20">
    <!-- Hero / Search Header -->
    <section class="relative py-20 bg-card/50 border-b border-app overflow-hidden">
        <div class="absolute inset-0 -z-10 bg-[radial-gradient(40%_40%_at_50%_0%,rgba(var(--core-brand-500),0.05)_0%,transparent_100%)]"></div>
        <div class="mx-auto max-w-4xl px-4 text-center">
            <h1 class="text-4xl font-extrabold text-header mb-4 sm:text-5xl">How can we help you today?</h1>
            <p class="text-body/80 text-lg mb-10">Search our knowledge base or browse by category to find the answers you need.</p>
            
            <div class="relative max-w-2xl mx-auto group">
                <Search class="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-body/80 group-focus-within:text-brand-500 transition-colors" />
                <input 
                    v-model="searchQuery" 
                    type="text" 
                    placeholder="Search for questions (e.g. billing, setup, api)..." 
                    class="w-full rounded-2xl border border-app bg-app px-14 py-5 text-header focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/10 transition-all shadow-xl" 
                />
            </div>
        </div>
    </section>

    <!-- Categories & Content -->
    <main class="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div class="flex flex-col lg:flex-row gap-12">
            <!-- Sidebar Navigation -->
            <aside class="w-full lg:w-64 flex-shrink-0">
                <div class="sticky top-24 space-y-1">
                    <button 
                        v-for="cat in categories" :key="cat.id"
                        @click="activeCategory = cat.id"
                        :class="[
                            activeCategory === cat.id ? 'bg-brand-500 text-white shadow-lg shadow-brand-500/20' : 'text-header hover:bg-muted',
                            'w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all'
                        ]"
                    >
                        <component :is="cat.icon" class="h-4 w-4" />
                        {{ cat.name }}
                    </button>
                    
                    <div class="mt-12 p-6 rounded-2xl bg-brand-500/5 border border-brand-500/10 text-center">
                        <div class="h-10 w-10 bg-brand-500/10 rounded-full flex items-center justify-center mx-auto mb-4 text-brand-600">
                            <MessageSquare class="h-5 w-5" />
                        </div>
                        <h4 class="text-sm font-bold text-header mb-2">Still need help?</h4>
                        <p class="text-xs text-body/80 mb-4 leading-relaxed">Our team is available 24/7 to assist with your specific needs.</p>
                        <button class="w-full py-2.5 rounded-lg bg-brand-500 text-white text-xs font-bold hover:bg-brand-600 transition-all">Contact Us</button>
                    </div>
                </div>
            </aside>

            <!-- FAQ List -->
            <div class="flex-1">
                <div v-if="filteredFaqs.length > 0" class="space-y-4">
                    <div v-for="(faq, index) in filteredFaqs" :key="index"
                        class="group rounded-2xl border border-app bg-card transition-all hover:border-brand-500/30 overflow-hidden"
                    >
                        <button 
                            @click="toggleFaq(faq)"
                            class="w-full flex items-center justify-between p-6 text-left"
                        >
                            <span class="font-bold text-header group-hover:text-brand-500 transition-colors">{{ faq.q }}</span>
                            <ChevronDown 
                                :class="[faq.open ? 'rotate-180 text-brand-500' : 'text-body/80']"
                                class="h-5 w-5 transition-transform duration-300" 
                            />
                        </button>
                        <transition 
                            enter-active-class="transition duration-300 ease-out" 
                            enter-from-class="transform -translate-y-2 opacity-0" 
                            enter-to-class="transform translate-y-0 opacity-100" 
                            leave-active-class="transition duration-150 ease-in" 
                            leave-from-class="transform translate-y-0 opacity-100" 
                            leave-to-class="transform -translate-y-2 opacity-0"
                        >
                            <div v-if="faq.open" class="px-6 pb-6 text-sm text-body/80 leading-relaxed border-t border-app/50 pt-4">
                                {{ faq.a }}
                            </div>
                        </transition>
                    </div>
                </div>
                <div v-else class="text-center py-20 bg-card rounded-3xl border border-app border-dashed">
                    <div class="h-16 w-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-6 text-body/80">
                        <Inbox class="h-8 w-8" />
                    </div>
                    <h3 class="text-xl font-bold text-header">No results found</h3>
                    <p class="text-body/80 mt-2">Try adjusting your search query or category filter.</p>
                    <button @click="searchQuery = ''; activeCategory = 'all'" class="mt-6 text-brand-500 font-bold hover:underline">Clear all filters</button>
                </div>
            </div>
        </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive } from 'vue'
import { 
    Search, 
    ChevronDown, 
    MessageSquare, 
    Inbox, 
    Globe, 
    Shield, 
    CreditCard, 
    Zap, 
    Settings 
} from 'lucide-vue-next'

definePageMeta({ name: 'FAQs' })

const searchQuery = ref('')
const activeCategory = ref('all')

const categories = [
    { id: 'all', name: 'All Questions', icon: Globe },
    { id: 'general', name: 'General', icon: Zap },
    { id: 'membership', name: 'Membership', icon: Shield },
    { id: 'billing', name: 'Billing', icon: CreditCard },
    { id: 'technical', name: 'Technical', icon: Settings },
]

const faqs = reactive([
    { 
        id: 1,
        cat: 'general',
        q: "What is Dephics Public?", 
        a: "Dephics Public is our centralized ecosystem gateway, providing unified access to marketing (Promo), communication (SMS), and resource management tools through a single, secure environment.",
        open: false
    },
    { 
        id: 2,
        cat: 'general',
        q: "Which regions are currently supported?", 
        a: "We currently support operations globally, with optimized server latency in East Africa, EU Central, and US East regions.",
        open: false
    },
    { 
        id: 3,
        cat: 'membership',
        q: "Can I upgrade my plan mid-cycle?", 
        a: "Yes! You can upgrade your plan at any time. The remaining value of your current plan will be automatically prorated toward your new selection.",
        open: false
    },
    { 
        id: 4,
        cat: 'billing',
        q: "What payment methods do you accept?", 
        a: "We accept all major credit cards, PayPal, and regional mobile money operators (M-Pesa, Tigo-Pesa) for East African customers.",
        open: false
    },
    { 
        id: 5,
        cat: 'technical',
        q: "Is there an API available for automation?", 
        a: "Full RESTful API access is available for Enterprise customers. Starter and Professional plans have limited access to webhook services.",
        open: false
    },
    { 
        id: 6,
        cat: 'membership',
        q: "Do you offer a free trial?", 
        a: "Our Starter plan is free forever for personal projects. Professional and Enterprise plans do not have a trial period, but we offer a 14-day money-back guarantee.",
        open: false
    },
    { 
        id: 7,
        cat: 'technical',
        q: "How secure is my data on Dephics?", 
        a: "We implement AES-256 encryption at rest and TLS 1.3 in transit. Our infrastructure is regularly audited for SOC2 and GDPR compliance.",
        open: false
    }
])

const filteredFaqs = computed(() => {
    return faqs.filter(faq => {
        const matchesSearch = faq.q.toLowerCase().includes(searchQuery.value.toLowerCase()) || 
                             faq.a.toLowerCase().includes(searchQuery.value.toLowerCase())
        const matchesCat = activeCategory.value === 'all' || faq.cat === activeCategory.value
        return matchesSearch && matchesCat
    })
})

const toggleFaq = (faq: any) => {
    faq.open = !faq.open
}
</script>

<style scoped>
.bg-card { background-color: rgb(var(--core-card-bg)); }
.bg-app { background-color: rgb(var(--core-app-bg)); }
.border-app { border-color: rgb(var(--core-app-border)); }
.text-header { color: rgb(var(--core-header-text)); }
.text-muted { color: rgb(var(--core-muted-text)); }
</style>
